//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';

import {
  ProductDetailsDrawerContent,
  ProductDetailsDrawerPanel,
} from '@campaigns/components/shared/product-details-modal/drawer/ProductDetailsDrawerContent';
import {
  SAMPLING_CAMPAIGNS_TRACKING_ID,
  SAMPLING_CAMPAIGN_PRODUCT_DRAWER_PREFIX,
} from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import {
  ProductModalMode,
  useProductModal,
} from '@campaigns/contexts/ProductModalContext';
import { useSamplingCampaign } from '@campaigns/hooks/useSamplingCampaign';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import {
  getInitialProduct,
  isMissingDetails,
  prepareProductToSubmit,
} from '@campaigns/utils/sampling/SamplingProductUtils';
import {
  PrimaryButton,
  PrimaryLinkButton,
  TertiaryButton,
} from '@components/atoms/button/Button';
import { CaptionSecondaryText } from '@components/atoms/text/Text';
import { RightDrawer } from '@components/molecules/right-drawer/RightDrawer';
import { Dialog } from '@components/primitives/dialog/Dialog';

// Field names owned by the Product Details panel (VariantsTab registers 'variations.N.*'
// for every variant, including index 0, so validating the whole form here would also
// validate hidden, possibly-incomplete rows on the Variants panel).
const PRODUCT_DETAILS_FIELD_NAMES = [
  'targetCatalogProductId',
  'name',
  'description',
  'shippingAvailabilityDate',
  'imgUrl',
  'brand',
  'category',
  'pdpUrl',
  'variations.0.quantity',
  'variations.0.fairMarketValue',
  'variations.0.logisticsRef',
] as const;

// New product selection experience, gated behind SamplingCampaignsProductSelectionRework.
// Reuses the existing tab content for now; look and feel will be reworked in follow-up PRs.
export const ProductDetailsDrawer = () => {
  const {
    addProductToCampaign,
    clearAllVariantImages,
    closeProductDetailsModal,
    handleMainImageSelect,
    isProductDetailsModalOpen,
    openProductDetailsModal,
    productModalState,
    setIsImageForAllVariants,
    uploadImageFilesFromProduct,
  } = useProductModal();
  const { selectedRetailer } = useSamplingCampaign();

  const { action, mode, onProductSave, product } = productModalState;
  const isViewMode = mode === ProductModalMode.View;
  const isEditMode = mode === ProductModalMode.Edit;
  const hasRequiredDetails = !isMissingDetails(product);

  const methods = useForm<EditableBVSamplingProduct>({
    // 'trigger()' from handleSaveAndAddVariants never flips RHF's isSubmitted flag,
    // so 'onSubmit' + reValidateMode:'onChange' would leave stale errors until re-triggered.
    mode: 'onChange',
    defaultValues: {
      ...productModalState.product,
      // Only set newProduct to true if in create mode or if explicitly set to true on the product
      newProduct:
        mode === ProductModalMode.Create ||
        productModalState.product?.newProduct,
      variations: productModalState.product?.variations ?? [],
    },
  });

  const hasMultipleVariants = methods.watch('variations').length > 1;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasInitialValidationTriggered, setHasInitialValidationTriggered] =
    useState(false);
  const [currentPanel, setCurrentPanel] = useState(
    ProductDetailsDrawerPanel.ProductDetails,
  );
  const isProductDetailsPanel =
    currentPanel === ProductDetailsDrawerPanel.ProductDetails;

  // Prevent closing (via header button, outside click, or Escape) while the submit is in flight
  const handleCloseDrawer = useCallback(() => {
    if (isSubmitting) {
      return;
    }

    closeProductDetailsModal();
  }, [closeProductDetailsModal, isSubmitting]);

  // Resolves to the saved product, or undefined if it could not be saved
  const saveProduct = useCallback(
    async (product: EditableBVSamplingProduct) => {
      // Note: toast notification on failure is shown by useUploadProductImagesMutation;
      // catch here only to stop the rejection from propagating as unhandled
      const dataWithUploadedImages = await uploadImageFilesFromProduct(
        product,
      ).catch(() => undefined);

      if (!dataWithUploadedImages) {
        return undefined;
      }

      const preparedProduct = prepareProductToSubmit(dataWithUploadedImages);

      if (isMissingDetails(preparedProduct)) {
        return undefined;
      }

      if (action === ProductModalMode.Edit && onProductSave) {
        return onProductSave(preparedProduct);
      }
      if (addProductToCampaign) {
        addProductToCampaign(
          preparedProduct,
          action === ProductModalMode.Edit,
          productModalState.originalProductId,
        );
      }

      return preparedProduct;
    },
    [
      uploadImageFilesFromProduct,
      addProductToCampaign,
      action,
      onProductSave,
      productModalState,
    ],
  );

  const onSubmit = useCallback(
    async (product: EditableBVSamplingProduct) => {
      setIsSubmitting(true);
      try {
        if (await saveProduct(product)) {
          closeProductDetailsModal();
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [saveProduct, closeProductDetailsModal],
  );

  // Saves the product, then reopens the drawer on the Product Details panel editing the saved product
  const onSaveAndBackToProduct = useCallback(
    async (product: EditableBVSamplingProduct) => {
      setIsSubmitting(true);
      try {
        const savedProduct = await saveProduct(product);
        if (!savedProduct) {
          return;
        }

        const savedUrls = new Set([
          savedProduct.imgUrl,
          ...(savedProduct.variations ?? []).map(
            (variation) => variation.imgUrl,
          ),
        ]);
        [
          product.imgUrl,
          ...product.variations.map((variation) => variation.imgUrl),
        ].forEach((url) => {
          if (url?.startsWith('blob:') && !savedUrls.has(url)) {
            URL.revokeObjectURL(url);
          }
        });
        // Selected files are now uploaded; drop them so they aren't uploaded again
        handleMainImageSelect(undefined);
        clearAllVariantImages();

        openProductDetailsModal(
          savedProduct,
          ProductModalMode.Edit,
          onProductSave,
        );
        methods.reset(getInitialProduct(savedProduct));
        setCurrentPanel(ProductDetailsDrawerPanel.ProductDetails);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      saveProduct,
      handleMainImageSelect,
      clearAllVariantImages,
      openProductDetailsModal,
      onProductSave,
      methods,
    ],
  );

  const shouldTriggerValidation =
    isEditMode && !hasRequiredDetails && !hasInitialValidationTriggered;

  const handleSaveAndAddVariants = useCallback(async () => {
    if (await methods.trigger(PRODUCT_DETAILS_FIELD_NAMES)) {
      setCurrentPanel(ProductDetailsDrawerPanel.Variants);
    }
  }, [methods]);

  useEffect(() => {
    if (shouldTriggerValidation) {
      setHasInitialValidationTriggered(true);
      void methods.handleSubmit(onSubmit)();
    }
  }, [shouldTriggerValidation, methods, onSubmit]);

  useEffect(() => {
    if (isProductDetailsModalOpen) {
      setCurrentPanel(ProductDetailsDrawerPanel.ProductDetails);

      // Default new products to sharing the parent image across all variants;
      // existing products keep reflecting whatever state their variant images are actually in.
      if (mode === ProductModalMode.Create) {
        setIsImageForAllVariants(true);
      }
    }
  }, [isProductDetailsModalOpen, mode, setIsImageForAllVariants]);

  const getDrawerTitle = () => {
    if (isViewMode) {
      return isProductDetailsPanel ? 'View Product' : 'View Variants';
    }

    if (!isProductDetailsPanel) {
      return 'Add Variants';
    }

    return mode === ProductModalMode.Create
      ? 'Create New Product'
      : 'Edit Product';
  };

  return (
    <FormProvider {...methods}>
      <RightDrawer
        closeDrawer={handleCloseDrawer}
        isDrawerOpen={isProductDetailsModalOpen}
        shouldCloseOnClickOutside={false}
        shouldCloseOnEsc={false}
        trackingIdPrefix={SAMPLING_CAMPAIGN_PRODUCT_DRAWER_PREFIX}
        width='medium'
      >
        <RightDrawer.Header>
          <RightDrawer.HeaderTitleGroup>
            <RightDrawer.HeaderTitle>
              {getDrawerTitle()}
            </RightDrawer.HeaderTitle>
            {mode === ProductModalMode.Create && (
              <Dialog.Description asChild>
                <p>
                  <CaptionSecondaryText>
                    Create a new product to sample in this campaign. Our team
                    will add it to{' '}
                    {selectedRetailer
                      ? `${selectedRetailer.bvClientId} catalog`
                      : 'your catalog'}{' '}
                    on request approval.
                  </CaptionSecondaryText>
                </p>
              </Dialog.Description>
            )}
          </RightDrawer.HeaderTitleGroup>
          <RightDrawer.HeaderClose
            isDisabled={isSubmitting}
            onClick={handleCloseDrawer}
          />
        </RightDrawer.Header>
        <RightDrawer.Body>
          <ProductDetailsDrawerContent
            currentPanel={currentPanel}
            isViewOnly={isViewMode}
          />
        </RightDrawer.Body>
        <RightDrawer.Footer>
          <FooterActionBar $hasLeadingAction={!isViewMode}>
            {isViewMode ? (
              <>
                {currentPanel === ProductDetailsDrawerPanel.Variants && (
                  <TertiaryButton
                    onClick={() =>
                      setCurrentPanel(ProductDetailsDrawerPanel.ProductDetails)
                    }
                    trackingId={
                      SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_DRAWER_BACK_BUTTON
                    }
                  >
                    Back
                  </TertiaryButton>
                )}
                {isProductDetailsPanel && hasMultipleVariants && (
                  <TertiaryButton
                    onClick={() =>
                      setCurrentPanel(ProductDetailsDrawerPanel.Variants)
                    }
                    trackingId={
                      SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_VARIANTS_TAB
                    }
                  >
                    View Variants
                  </TertiaryButton>
                )}
                <PrimaryButton
                  onClick={handleCloseDrawer}
                  trackingId={
                    SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_CANCEL_BUTTON
                  }
                >
                  Close
                </PrimaryButton>
              </>
            ) : (
              <>
                {isProductDetailsPanel ? (
                  <PrimaryLinkButton
                    isDisabled={isSubmitting}
                    onClick={handleSaveAndAddVariants}
                    trackingId={
                      isEditMode
                        ? SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_DRAWER_SAVE_AND_EDIT_VARIANTS_BUTTON
                        : SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_DRAWER_SAVE_AND_ADD_VARIANTS_BUTTON
                    }
                  >
                    {isEditMode
                      ? 'Save and Edit Variants'
                      : 'Save and Add Variants'}
                  </PrimaryLinkButton>
                ) : (
                  <PrimaryLinkButton
                    isDisabled={isSubmitting}
                    onClick={methods.handleSubmit(onSaveAndBackToProduct)}
                    trackingId={
                      SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_DRAWER_BACK_BUTTON
                    }
                  >
                    Save and back to Product
                  </PrimaryLinkButton>
                )}
                <TertiaryButton
                  isDisabled={isSubmitting}
                  onClick={handleCloseDrawer}
                  trackingId={
                    SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_CANCEL_BUTTON
                  }
                >
                  Cancel
                </TertiaryButton>
                {isProductDetailsPanel ? (
                  <PrimaryButton
                    isDisabled={isSubmitting}
                    onClick={methods.handleSubmit(onSubmit)}
                    trackingId={
                      SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_SUBMIT_BUTTON
                    }
                  >
                    Save Product
                  </PrimaryButton>
                ) : (
                  <PrimaryButton
                    isDisabled={isSubmitting}
                    onClick={methods.handleSubmit(onSubmit)}
                    trackingId={
                      SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_DRAWER_SAVE_VARIANTS_BUTTON
                    }
                  >
                    Save Variants
                  </PrimaryButton>
                )}
              </>
            )}
          </FooterActionBar>
        </RightDrawer.Footer>
      </RightDrawer>
    </FormProvider>
  );
};

const FooterActionBar = styled(RightDrawer.FooterActionBar)<{
  $hasLeadingAction: boolean;
}>`
  ${({ $hasLeadingAction }) =>
    $hasLeadingAction && '& > :first-child { margin-right: auto; }'}
`;
